// Custom Field Types for WTF Amplify CRM

export type CustomFieldType =
  | 'TEXT'
  | 'NUMBER'
  | 'DATE'
  | 'DROPDOWN'
  | 'CHECKBOX'
  | 'MULTI_SELECT'
  | 'URL'
  | 'EMAIL'
  | 'PHONE'
  | 'CURRENCY'
  | 'TEXTAREA';

export interface CustomFieldValidation {
  required?: boolean;
  min?: number;
  max?: number;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
}

export interface CustomFieldDefinition {
  id: string;
  name: string;
  key: string;
  fieldType: CustomFieldType;
  description?: string;
  options?: string[];
  validation?: CustomFieldValidation;
  defaultValue?: any;
  isActive: boolean;
  isSearchable: boolean;
  isFilterable: boolean;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export interface CreateCustomFieldRequest {
  name: string;
  key?: string;
  fieldType: CustomFieldType;
  description?: string;
  options?: string[];
  validation?: CustomFieldValidation;
  defaultValue?: any;
  isActive?: boolean;
  isSearchable?: boolean;
  isFilterable?: boolean;
  sortOrder?: number;
}

export interface UpdateCustomFieldRequest {
  name?: string;
  description?: string;
  options?: string[];
  validation?: CustomFieldValidation;
  defaultValue?: any;
  isActive?: boolean;
  isSearchable?: boolean;
  isFilterable?: boolean;
  sortOrder?: number;
}

export interface CustomFieldValue {
  fieldId: string;
  value: any;
}

export interface LeadCustomFields {
  leadId: string;
  values: Record<string, any>;
}

// Field type configuration for UI
export interface FieldTypeConfig {
  type: CustomFieldType;
  label: string;
  icon: string;
  description: string;
  supportsOptions: boolean;
  supportsValidation: {
    required: boolean;
    min: boolean;
    max: boolean;
    minLength: boolean;
    maxLength: boolean;
    pattern: boolean;
  };
}

export const FIELD_TYPE_CONFIGS: FieldTypeConfig[] = [
  {
    type: 'TEXT',
    label: 'Text',
    icon: 'Type',
    description: 'Single line text input',
    supportsOptions: false,
    supportsValidation: {
      required: true,
      min: false,
      max: false,
      minLength: true,
      maxLength: true,
      pattern: true,
    },
  },
  {
    type: 'TEXTAREA',
    label: 'Textarea',
    icon: 'AlignLeft',
    description: 'Multi-line text input',
    supportsOptions: false,
    supportsValidation: {
      required: true,
      min: false,
      max: false,
      minLength: true,
      maxLength: true,
      pattern: false,
    },
  },
  {
    type: 'NUMBER',
    label: 'Number',
    icon: 'Hash',
    description: 'Numeric input',
    supportsOptions: false,
    supportsValidation: {
      required: true,
      min: true,
      max: true,
      minLength: false,
      maxLength: false,
      pattern: false,
    },
  },
  {
    type: 'CURRENCY',
    label: 'Currency',
    icon: 'IndianRupee',
    description: 'Currency amount',
    supportsOptions: false,
    supportsValidation: {
      required: true,
      min: true,
      max: true,
      minLength: false,
      maxLength: false,
      pattern: false,
    },
  },
  {
    type: 'DATE',
    label: 'Date',
    icon: 'Calendar',
    description: 'Date picker',
    supportsOptions: false,
    supportsValidation: {
      required: true,
      min: false,
      max: false,
      minLength: false,
      maxLength: false,
      pattern: false,
    },
  },
  {
    type: 'DROPDOWN',
    label: 'Dropdown',
    icon: 'ChevronDown',
    description: 'Single select from options',
    supportsOptions: true,
    supportsValidation: {
      required: true,
      min: false,
      max: false,
      minLength: false,
      maxLength: false,
      pattern: false,
    },
  },
  {
    type: 'MULTI_SELECT',
    label: 'Multi-select',
    icon: 'CheckSquare',
    description: 'Multiple selections from options',
    supportsOptions: true,
    supportsValidation: {
      required: true,
      min: false,
      max: false,
      minLength: false,
      maxLength: false,
      pattern: false,
    },
  },
  {
    type: 'CHECKBOX',
    label: 'Checkbox',
    icon: 'Check',
    description: 'Yes/No toggle',
    supportsOptions: false,
    supportsValidation: {
      required: true,
      min: false,
      max: false,
      minLength: false,
      maxLength: false,
      pattern: false,
    },
  },
  {
    type: 'EMAIL',
    label: 'Email',
    icon: 'Mail',
    description: 'Email address input',
    supportsOptions: false,
    supportsValidation: {
      required: true,
      min: false,
      max: false,
      minLength: false,
      maxLength: true,
      pattern: false,
    },
  },
  {
    type: 'PHONE',
    label: 'Phone',
    icon: 'Phone',
    description: 'Phone number input',
    supportsOptions: false,
    supportsValidation: {
      required: true,
      min: false,
      max: false,
      minLength: false,
      maxLength: true,
      pattern: true,
    },
  },
  {
    type: 'URL',
    label: 'URL',
    icon: 'Link',
    description: 'Web address input',
    supportsOptions: false,
    supportsValidation: {
      required: true,
      min: false,
      max: false,
      minLength: false,
      maxLength: true,
      pattern: false,
    },
  },
];
